import JSZip from "jszip";

import { buildKml, buildMergedGpx, buildSplitFiles, getSplitBoundaryPoints, parseGpxFile } from "./gpx.js";
import { createMap } from "./map.js";
import { baseFileName, createDownloadUrl, formatDistance } from "./utils.js";

export function bootInstance(root) {
  const mode = root.dataset.mode;
  const mapEl = root.querySelector(".gpxtr__map");
  const mapContainer = root.querySelector(".gpxtr__mapContainer");
  const controls = root.querySelector(".gpxtr__controls");
  const input = root.querySelector(".gpxtr__gpxInput");
  const dropzone = root.querySelector(".gpxtr__dropzone");
  const splitCountInput = root.querySelector(".gpxtr__splitCount");
  const actionBtn = root.querySelector(".gpxtr__actionBtn");
  const status = root.querySelector(".gpxtr__status");
  const stats = root.querySelector(".gpxtr__stats");
  const fileList = root.querySelector(".gpxtr__fileList");
  const downloads = root.querySelector(".gpxtr__downloads");
  const height = root.dataset.height || "600px";

  mapEl.style.height = height;
  mapContainer.style.height = height;

  let mapApi = null;
  let currentProfile = null;
  let currentProfiles = [];
  let downloadUrls = [];

  try {
    mapApi = createMap(mapEl);
  } catch (error) {
    setStatus(error.message || "Unable to initialize the map.", true);
    console.error(error);
    return;
  }

  bindUploadEvents();
  bindAction();

  if (splitCountInput) {
    splitCountInput.addEventListener("input", () => {
      if (!currentProfile) {
        return;
      }

      clearDownloads();
      renderSplitPreview();
      renderSplitStats();
    });
  }

  function bindUploadEvents() {
    input.addEventListener("change", (event) => {
      const files = Array.from(event.target.files || []);
      if (files.length) {
        loadFiles(files);
      }
      event.target.value = "";
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.add("dragover");
      });
    });

    ["dragleave", "drop"].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.remove("dragover");
      });
    });

    dropzone.addEventListener("drop", (event) => {
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length) {
        loadFiles(files);
      }
    });
  }

  function bindAction() {
    actionBtn.addEventListener("click", () => {
      if (mode === "split") {
        downloadSplitZip();
        return;
      }

      if (mode === "merge") {
        generateMergedFile();
        return;
      }

      generateKmlFile();
    });
  }

  async function loadFiles(files) {
    clearDownloads();
    clearPanels();
    setActionEnabled(false);
    setStatus("Reading GPX data...", false);

    try {
      if (mode === "merge") {
        await loadMergeFiles(files);
        return;
      }

      currentProfile = await parseGpxFile(files[0]);
      currentProfiles = [currentProfile];
      renderSingleProfile();
      setActionEnabled(true);
      setStatus(`Loaded: ${formatDistance(currentProfile.totalDistance)}`, false);
    } catch (error) {
      resetState();
      setStatus(error.message || "Unable to read the GPX file.", true);
      console.error(error);
    }
  }

  async function loadMergeFiles(files) {
    if (files.length < 2) {
      throw new Error("Please upload at least two GPX files to merge.");
    }

    currentProfiles = [];
    for (const file of files) {
      currentProfiles.push(await parseGpxFile(file));
    }

    currentProfile = null;
    renderMergeMap();
    renderFileList();
    renderMergeStats();
    setActionEnabled(true);
    setStatus(`Loaded ${currentProfiles.length} GPX files.`, false);
  }

  function renderSingleProfile() {
    if (mode === "split") {
      renderSplitPreview();
      renderSplitStats();
      return;
    }

    mapApi.clear();
    mapApi.addProfile(currentProfile, { color: mapApi.getColor(0) });
    mapApi.fit();
    renderStats([
      ["Distance", formatDistance(currentProfile.totalDistance)],
      ["Output", "KML"],
    ]);
  }

  function renderSplitPreview() {
    const fileCount = getSplitCount();

    mapApi.clear();
    mapApi.addProfile(currentProfile, { color: mapApi.getColor(0) });
    mapApi.addSplitMarkers(getSplitBoundaryPoints(currentProfile, fileCount));
    mapApi.fit();
  }

  function renderMergeMap() {
    mapApi.clear();
    currentProfiles.forEach((profile, index) => {
      mapApi.addProfile(profile, { color: mapApi.getColor(index) });
    });
    mapApi.fit();
  }

  async function downloadSplitZip() {
    if (!currentProfile) {
      return;
    }

    const fileCount = getSplitCount();
    const files = buildSplitFiles(currentProfile, fileCount);
    const zip = new JSZip();

    files.forEach((file) => {
      zip.file(file.fileName, file.text);
    });

    setActionEnabled(false);
    setStatus("Creating ZIP download...", false);

    try {
      const blob = await zip.generateAsync({ type: "blob" });
      triggerBlobDownload(blob, `${baseFileName(currentProfile.fileName, "track")}-split.zip`);
      setStatus(`Downloaded ${fileCount} GPX files as one ZIP. Each part is about ${formatDistance(currentProfile.totalDistance / fileCount)}.`, false);
    } catch (error) {
      setStatus(error.message || "Unable to create the ZIP download.", true);
      console.error(error);
    } finally {
      setActionEnabled(true);
    }
  }

  function generateMergedFile() {
    if (currentProfiles.length < 2) {
      return;
    }

    triggerFileDownload(buildMergedGpx(currentProfiles));
    setStatus(`Downloaded ${currentProfiles.length} merged GPX files as one file.`, false);
  }

  function generateKmlFile() {
    if (!currentProfile) {
      return;
    }

    triggerFileDownload(buildKml(currentProfile));
    setStatus("Downloaded KML file.", false);
  }

  function renderFileList() {
    fileList.innerHTML = "";
    fileList.hidden = !currentProfiles.length;

    currentProfiles.forEach((profile, index) => {
      const row = document.createElement("div");
      row.className = "gpxtr__fileRow";

      const color = document.createElement("span");
      color.className = "gpxtr__fileColor";
      color.style.backgroundColor = mapApi.getColor(index);

      const name = document.createElement("span");
      name.className = "gpxtr__fileName";
      name.textContent = profile.fileName;

      const distance = document.createElement("span");
      distance.className = "gpxtr__fileDistance";
      distance.textContent = formatDistance(profile.totalDistance);

      const up = createOrderButton("Up", index === 0, () => moveProfile(index, -1));
      const down = createOrderButton("Down", index === currentProfiles.length - 1, () => moveProfile(index, 1));

      row.append(color, name, distance, up, down);
      fileList.append(row);
    });
  }

  function createOrderButton(label, disabled, onClick) {
    const button = document.createElement("button");
    button.className = "gpxtr__orderBtn";
    button.type = "button";
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener("click", onClick);
    return button;
  }

  function moveProfile(index, direction) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= currentProfiles.length) {
      return;
    }

    const nextProfiles = currentProfiles.slice();
    [nextProfiles[index], nextProfiles[nextIndex]] = [nextProfiles[nextIndex], nextProfiles[index]];
    currentProfiles = nextProfiles;
    clearDownloads();
    renderMergeMap();
    renderFileList();
    setStatus("Merge order updated.", false);
  }

  function renderSplitStats() {
    const fileCount = getSplitCount();
    renderStats([
      ["Distance", formatDistance(currentProfile.totalDistance)],
      ["Files", String(fileCount)],
      ["Per file", formatDistance(currentProfile.totalDistance / fileCount)],
    ]);
  }

  function renderMergeStats() {
    const totalDistance = currentProfiles.reduce((total, profile) => total + profile.totalDistance, 0);
    renderStats([
      ["Files", String(currentProfiles.length)],
      ["Total distance", formatDistance(totalDistance)],
      ["Output", "GPX"],
    ]);
  }

  function renderStats(items) {
    stats.innerHTML = "";
    items.forEach(([label, value]) => {
      const item = document.createElement("div");
      item.className = "gpxtr__stat";

      const labelEl = document.createElement("span");
      labelEl.textContent = label;

      const valueEl = document.createElement("strong");
      valueEl.textContent = value;

      item.append(labelEl, valueEl);
      stats.append(item);
    });
    stats.hidden = false;
  }

  function renderDownloads(files, labelCallback) {
    clearDownloads();
    downloads.hidden = false;

    files.forEach((file, index) => {
      const link = document.createElement("a");
      const url = createDownloadUrl(file.text, file.type);
      downloadUrls.push(url);
      link.className = "gpxtr__download btn btn--medium btn--uppercase";
      link.href = url;
      link.download = file.fileName;
      link.textContent = labelCallback(file, index);
      downloads.append(link);
    });
  }

  function triggerBlobDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    downloadUrls.push(url);
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.append(link);
    link.click();
    link.remove();
  }

  function triggerFileDownload(file) {
    const url = createDownloadUrl(file.text, file.type);
    const link = document.createElement("a");
    downloadUrls.push(url);
    link.href = url;
    link.download = file.fileName;
    link.style.display = "none";
    document.body.append(link);
    link.click();
    link.remove();
  }

  function getSplitCount() {
    const value = Number.parseInt(splitCountInput?.value || "2", 10);
    return Math.max(2, Math.min(50, Number.isFinite(value) ? value : 2));
  }

  function setActionEnabled(enabled) {
    actionBtn.disabled = !enabled;
    if (enabled) {
      actionBtn.removeAttribute("aria-disabled");
      return;
    }

    actionBtn.setAttribute("aria-disabled", "true");
  }

  function setStatus(message, isError) {
    status.textContent = message;
    status.classList.toggle("gpxtr__status--error", Boolean(isError));
  }

  function clearDownloads() {
    downloadUrls.forEach((url) => URL.revokeObjectURL(url));
    downloadUrls = [];
    downloads.innerHTML = "";
    downloads.hidden = true;
  }

  function clearPanels() {
    stats.innerHTML = "";
    stats.hidden = true;
    fileList.innerHTML = "";
    fileList.hidden = true;
  }

  function resetState() {
    currentProfile = null;
    currentProfiles = [];
    mapApi.clear();
    clearPanels();
    setActionEnabled(false);
  }
}
